import React, { useMemo, useState } from 'react';
import { SlideDown } from 'react-slidedown';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { AirFareConditions } from 'models/flightValidation';

import {
  Arrow,
  Card,
  Container,
  Content,
  Disclaimer,
  FlightTitle,
  FlightPolicy,
  FlightPolicyValue,
  Header,
  Title,
} from './styles';

interface Props {
  policies: AirFareConditions[];
}

const CancellationPolicies = ({ policies }: Props) => {
  const { t } = useStoreTranslation();

  const [showPolicies, setShowPolicies] = useState(false);

  const toggleShowPolicies = () => setShowPolicies(!showPolicies);
  const title = useMemo(() => `${t('flightDetails.cancellationPolicies.title2')} ${policies[0].flight}`, [policies, t]);

  const buildPenaltyText = (
    allowed: boolean,
    allowedAfter: boolean,
    amount?: { currencyCode: string; fare: number; taxes: number },
    amountAfter?: { currencyCode: string; fare: number; taxes: number },
    isChange?: boolean
  ) => {
    const buildDetail = (
      allowedValue: boolean,
      amountValue?: { currencyCode: string; fare: number; taxes: number }
    ) => {
      
      if (!allowedValue)
        return t('flightDetails.cancellationPolicies.change.notAllowed');

      const total = (amountValue?.fare || 0) + (amountValue?.taxes || 0);
      let penaltyText = '';

      if (total > 0) {
        isChange
          ? penaltyText = t('flightDetails.cancellationPolicies.change.changePenalty', {
              currency: amountValue?.currencyCode,
              amount: total,
            })
          : penaltyText = t('flightDetails.cancellationPolicies.cancellation.cancelPenalty', {
              currency: amountValue?.currencyCode,
              amount: total,
            });
      }

      if (isChange) {
        if (!allowed && !allowedAfter)
          return t('flightDetails.cancellationPolicies.change.notAllowed');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes > 0) || (amountAfter && amountAfter.fare + amountAfter.taxes > 0))
          return t('flightDetails.cancellationPolicies.change.allowedWithPenalty') + (penaltyText ? ` ${penaltyText}` : '');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes === 0) && (amountAfter && amountAfter.fare + amountAfter.taxes === 0))
          return t('flightDetails.cancellationPolicies.change.allowedWithoutPenalty');

        return t('flightDetails.cancellationPolicies.change.notAllowed');
      }
      else {
        if (!allowed && !allowedAfter)
          return t('flightDetails.cancellationPolicies.cancellation.notAllowed');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes > 0) || (amountAfter && amountAfter.fare + amountAfter.taxes > 0))
          return t('flightDetails.cancellationPolicies.cancellation.allowedWithPenalty') + (penaltyText ? ` ${penaltyText}` : '');
        if ((allowed || allowedAfter) && (amount && amount.fare + amount.taxes === 0) && (amountAfter && amountAfter.fare + amountAfter.taxes === 0))
          return t('flightDetails.cancellationPolicies.cancellation.allowedWithoutPenalty');

        return t('flightDetails.cancellationPolicies.cancellation.notAllowed');
      }
    };

    const beforeDetail = buildDetail(allowed, amount);
    const afterDetail = buildDetail(allowedAfter, amountAfter);

    /* compara allowed + amount */
    const sameConditions =
      (allowed === allowedAfter &&
      ((amount?.fare || 0) + (amount?.taxes || 0) === (amountAfter?.fare || 0) + (amountAfter?.taxes || 0)));

    if (sameConditions) {
      return beforeDetail;
    }

    return `${t(
        'flightDetails.before'
      )} ${beforeDetail}, ${t(
        'flightDetails.after'
      )} ${afterDetail}`;
  };

  return (
    <Card>
      <Header onClick={toggleShowPolicies}>
        <Title>{title}</Title>
        <Arrow className={showPolicies ? 'active' : ''} />
      </Header>
      <SlideDown>
        {showPolicies && (
          <Container>
            {policies.map((policy, index) => (
              <Content key={index}>
                <FlightTitle>{`${t('flightDetails.cancellationPolicies.flight')} ${policy.flight}`}</FlightTitle>
                <p>
                  <FlightPolicy>{`${t('flightDetails.cancellationPolicies.change.title')}: `}</FlightPolicy>
                  <FlightPolicyValue>
                    {buildPenaltyText(policy.changesAllowed, policy.changesAllowedAfter, policy.changesAmount, policy.changesAmountAfter, true)}
                  </FlightPolicyValue>
                </p>
                <p>
                  <FlightPolicy>{`${t('flightDetails.cancellationPolicies.cancellation.title')}: `}</FlightPolicy>
                  <FlightPolicyValue>
                    {buildPenaltyText(policy.cancellationAllowed, policy.cancellationAllowedAfter, policy.cancellationAmount, policy.cancellationAmountAfter, false)}
                  </FlightPolicyValue>
                </p>
                <p>
                  <FlightPolicy>{`${t('flightDetails.cancellationPolicies.noShow.title')}: `}</FlightPolicy>
                  <FlightPolicyValue>
                    <>
                      {`${t('flightDetails.cancellationPolicies.noShow.beginText')} 
                      ${
                        policy.noShow === 0
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-0')
                          : policy.noShow === 1
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-1')
                          : policy.noShow === 2
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-2')
                          : policy.noShow === 3
                          ? t('flightDetails.cancellationPolicies.noShow.noshow-3')
                          : ''
                      }. ${t('flightDetails.cancellationPolicies.noShow.endText')}`}
                    </>
                  </FlightPolicyValue>
                </p>
              </Content>
            ))}
            <Disclaimer>{t('flightDetails.cancellationPolicies.disclaimer')}</Disclaimer>
          </Container>
        )}
      </SlideDown>
    </Card>
  );
};

export default CancellationPolicies;
